/**
 * ============================================================================
 * API Client
 * ============================================================================
 *
 * Typed fetch client for the /api/* account management endpoints.
 * All requests include the Clerk session JWT for authentication.
 */

import type { ApiKeyInfo, ApiKeyCreated } from "@repo/travel-core";

async function fetchApi<T>(
  path: string,
  getToken: () => Promise<string | null>,
  options: RequestInit = {},
): Promise<T> {
  const token = await getToken();
  if (!token) {
    throw new Error("Not authenticated");
  }

  const res = await fetch(path, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: "Request failed" }));
    throw new Error((body as { error?: string }).error || `HTTP ${res.status}`);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  return res.json() as Promise<T>;
}

// ============================================================================
// API Keys
// ============================================================================

export async function createApiKey(
  getToken: () => Promise<string | null>,
  name?: string,
): Promise<ApiKeyCreated> {
  return fetchApi<ApiKeyCreated>("/api/keys", getToken, {
    method: "POST",
    body: JSON.stringify({ name }),
  });
}

export async function listApiKeys(
  getToken: () => Promise<string | null>,
): Promise<{ keys: ApiKeyInfo[] }> {
  return fetchApi<{ keys: ApiKeyInfo[] }>("/api/keys", getToken);
}

export async function revokeApiKey(
  getToken: () => Promise<string | null>,
  id: number,
): Promise<void> {
  return fetchApi<void>(`/api/keys/${id}`, getToken, { method: "DELETE" });
}

export async function revealApiKey(
  getToken: () => Promise<string | null>,
  id: number,
): Promise<{ key: string }> {
  return fetchApi<{ key: string }>(`/api/keys/${id}/reveal`, getToken);
}
